/**
 * Lista canônica de tipos de imóvel do sistema.
 * Fonte única de verdade — usada por:
 *   - Zod schema (validação de API)
 *   - Componentes de formulário e filtros
 *   - Mapeamento VRSync ↔ PT (import/export XML)
 *   - Função generate_property_code_next (prefixos)
 */

export const PROPERTY_TYPES = [
  // ── Residencial ──────────────────────────────────────────────────────────
  { value: 'apartamento',          label: 'Apartamento',           prefix: 'APT',  group: 'Residencial' },
  { value: 'casa',                 label: 'Casa',                  prefix: 'CASA', group: 'Residencial' },
  { value: 'sobrado',              label: 'Sobrado',               prefix: 'SOB',  group: 'Residencial' },
  { value: 'cobertura',            label: 'Cobertura',             prefix: 'COB',  group: 'Residencial' },
  { value: 'kitnet',               label: 'Kitnet',                prefix: 'KIT',  group: 'Residencial' },
  { value: 'studio',               label: 'Studio',                prefix: 'STU',  group: 'Residencial' },
  { value: 'flat',                 label: 'Flat',                  prefix: 'FLAT', group: 'Residencial' },
  { value: 'casa em condomínio',   label: 'Casa em Condomínio',    prefix: 'CC',   group: 'Residencial' },
  { value: 'sobrado em condomínio',label: 'Sobrado em Condomínio', prefix: 'SC',   group: 'Residencial' },
  { value: 'lote em condomínio',   label: 'Lote em Condomínio',    prefix: 'LC',   group: 'Residencial' },
  { value: 'terreno',              label: 'Terreno',               prefix: 'TER',  group: 'Residencial' },
  { value: 'lote',                 label: 'Lote',                  prefix: 'LOTE', group: 'Residencial' },
  { value: 'chácara',              label: 'Chácara',               prefix: 'CHA',  group: 'Residencial' },
  { value: 'fazenda',              label: 'Fazenda',               prefix: 'FAZ',  group: 'Residencial' },
  { value: 'vila',                 label: 'Vila',                  prefix: 'VILA', group: 'Residencial' },
  // ── Comercial ────────────────────────────────────────────────────────────
  { value: 'sala',                 label: 'Sala Comercial',        prefix: 'SALA', group: 'Comercial'   },
  { value: 'loja',                 label: 'Loja',                  prefix: 'LOJA', group: 'Comercial'   },
  { value: 'galpão',               label: 'Galpão',                prefix: 'GAL',  group: 'Comercial'   },
  { value: 'prédio',               label: 'Prédio / Edifício',     prefix: 'PRED', group: 'Comercial'   },
  { value: 'consultório',          label: 'Consultório',           prefix: 'CONS', group: 'Comercial'   },
  { value: 'hotel',                label: 'Hotel',                 prefix: 'HTL',  group: 'Comercial'   },
  { value: 'garagem',              label: 'Garagem',               prefix: 'GAR',  group: 'Comercial'   },
] as const

export type PropertyType = typeof PROPERTY_TYPES[number]['value']

/** Array de valores para z.enum() */
export const PROPERTY_TYPE_VALUES = PROPERTY_TYPES.map(t => t.value) as
  [PropertyType, ...PropertyType[]]

/** Map valor → prefixo (espelha a lógica da função generate_property_code_next) */
export const PROPERTY_TYPE_PREFIXES: Record<string, string> = Object.fromEntries(
  PROPERTY_TYPES.map(t => [t.value, t.prefix])
)

/**
 * Mapeamento VRSync (EN) → tipo PT do banco.
 * Cobre todos os PropertyType da especificação GrupoZAP/OLX.
 * Referência: https://developers.grupozap.com/feeds/vrsync/elements/details.html#property-type
 */
export const VRSYNC_TO_PT: Record<string, PropertyType> = {
  // Residencial
  'Residential / Apartment':    'apartamento',
  'Residential / Home':         'casa',
  'Residential / Sobrado':      'sobrado',
  'Residential / Condo':        'casa em condomínio',  // ← era "condomínio" (inválido)
  'Residential / Penthouse':    'cobertura',
  'Residential / Flat':         'flat',
  'Residential / Kitnet':       'kitnet',
  'Residential / Studio':       'studio',
  'Residential / Land Lot':     'terreno',
  'Residential / Village House':'vila',
  'Residential / Farm Ranch':   'fazenda',
  'Residential / Agricultural': 'chácara',
  // Comercial
  'Commercial / Office':           'sala',
  'Commercial / Loja':             'loja',
  'Commercial / Business':         'loja',
  'Commercial / Industrial':       'galpão',
  'Commercial / Building':         'prédio',
  'Commercial / Edificio Comercial':'prédio',
  'Commercial / Edificio Residencial':'prédio',
  'Commercial / Consultorio':      'consultório',
  'Commercial / Hotel':            'hotel',
  'Commercial / Garage':           'garagem',
  'Commercial / Corporate Floor':  'sala',
  'Commercial / Land Lot':         'terreno',
}

/**
 * Mapeamento PT → VRSync (EN) para exportação de feeds XML.
 * Inverso do VRSYNC_TO_PT.
 */
export const PT_TO_VRSYNC: Record<string, string> = {
  'apartamento':           'Residential / Apartment',
  'casa':                  'Residential / Home',
  'sobrado':               'Residential / Sobrado',
  'cobertura':             'Residential / Penthouse',
  'kitnet':                'Residential / Kitnet',
  'studio':                'Residential / Studio',
  'flat':                  'Residential / Flat',
  'casa em condomínio':    'Residential / Condo',
  'sobrado em condomínio': 'Residential / Sobrado',
  'lote em condomínio':    'Residential / Land Lot',
  'terreno':               'Residential / Land Lot',
  'lote':                  'Residential / Land Lot',
  'chácara':               'Residential / Agricultural',
  'fazenda':               'Residential / Farm Ranch',
  'vila':                  'Residential / Village House',
  'sala':                  'Commercial / Office',
  'loja':                  'Commercial / Loja',
  'galpão':                'Commercial / Industrial',
  'prédio':                'Commercial / Building',
  'consultório':           'Commercial / Consultorio',
  'hotel':                 'Commercial / Hotel',
  'garagem':               'Commercial / Garage',
}
